import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import intl from 'react-intl-universal';
import logger from 'utils/logger';

// @mui material components
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Typography } from '@mui/material';

// Material Dashboard 2 PRO React components
import MDInput from 'components/MDInput';
import MDLoadingButton from 'components/MDLoadingButton';

// Authentication layout components
import BasicLayout from './components/BasicLayout';

// Images
import newBgImage from 'assets/images/bg-login-bai.jpeg';

import { useAuth } from 'views/root/AuthProvider';
import { IWG_GROUP } from 'views/root/constants';
import { zIndexValues } from 'utils/styles';
// For change input background color
const StyledMDInput = styled(MDInput)(() => ({
  background: '#FFFFFF !important',
  color: 'black !important',
  borderRadius: '10px',
}));

const MFA_TYPE = {
  EMAIL: 'EMAIL',
  TOTP: 'TOTP',
  NOMFA: 'DOCLOUD',
};

const SIGN_IN_STEP = {
  DONE: 'DONE',
  CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE:
    'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE',
  CONFIRM_SIGN_IN_WITH_TOTP_CODE: 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
};

// Create a logger instance for this component
const log = logger.createLogger('Welcome');

function WelcomePage() {
  const navigate = useNavigate();
  const { login, currentUserInfo, userGroups, isAuthenticated, authError } =
    useAuth();

  const AUTH_ERROR_MESSAGES = {
    404: 'login_user_not_found',
    403: 'login_incorrect_username_password',
  };

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    OTP: '',
  });
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isAuthError, setIsAuthError] = useState(false);
  const [showWrongPasswordWarning, setShowWrongPasswordWarning] =
    useState(false);
  const [showWrongOTPWarning, setShowWrongOTPWarning] = useState(false);
  const [wrongOTPFor3Times, setWrongOTPFor3Times] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showOTPInputBox, setShowOTPInputBox] = useState({
    ifShow: false,
    otpType: '',
  });

  const pallete = [
    {
      cardBackgroundColor: 'rgba(52, 52, 52, 0.8)',
      mainBoxBackgroundColor: '#194796',
      loadingButtonColor: '#194796',
    },
    {
      cardBackgroundColor: '#8B8B8B',
      mainBoxBackgroundColor: '#010101',
      loadingButtonColor: '#C81414',
    },
  ];

  useEffect(() => {
    if (isAuthenticated) {
      if (
        currentUserInfo ||
        (!currentUserInfo && userGroups?.includes(IWG_GROUP))
      ) {
        navigate('/');
      }
    }
    if (authError) {
      setIsSigningIn(false);
      setIsAuthError(true);
      // Reload page after 3 seconds
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }, [currentUserInfo, userGroups, navigate, isAuthenticated, authError]);

  useEffect(() => {
    // when wrongOTPFor3Times, refresh page after 3 seconds;
    if (wrongOTPFor3Times) {
      setTimeout(() => {
        resetStates();
      }, 3000);
    }
  }, [wrongOTPFor3Times]);

  const resetStates = () => {
    setFormData({ username: '', password: '', OTP: '' });
    setShowWrongPasswordWarning(false);
    setShowWrongOTPWarning(false);
    setWrongOTPFor3Times(false);
    setShowPassword(false);
    setShowOTPInputBox({
      ifShow: false,
      otpType: '',
    });
  };

  const handleSuccessfulSignIn = async () => {
    try {
      // Clear any warnings
      setShowWrongPasswordWarning(false);
      await login();
    } catch (error) {
      log.error('Error sending signin data', { error });
    }
  };

  const cognitoSignIn = async () => {
    setIsSigningIn(true);
    setIsAuthError(false);

    //! With Secondary password
    if (formData.OTP) {
      // text input box for One-Time-Password is shown; user needs to verify the OTP;
      try {
        const { nextStep } = await confirmSignIn({
          challengeResponse: formData.OTP,
        });

        if (nextStep.signInStep === SIGN_IN_STEP.DONE) {
          log.info('cognitoSignIn with OTP - DONE');
          await handleSuccessfulSignIn();
        } else {
          if (
            nextStep.signInStep ===
            SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE
          ) {
            formData.OTP = MFA_TYPE.NOMFA;
            cognitoSignIn();
          } else {
            // OTP is wrong; auth failed;
            log.info('cognitoSignIn with OTP - NOT DONE');
            setShowWrongOTPWarning(true);
            setIsSigningIn(false);
          }
        }
      } catch (error) {
        log.error('Error occurred', error);
        log.error('Error occurred', { error: error.message });
        if (
          error.name &&
          ['NotAuthorizedException', 'CodeMismatchException'].includes(
            error.name
          )
        ) {
          // User has exceeded the limit of trials for verification code.
          setShowWrongOTPWarning(false);
          setWrongOTPFor3Times(true);
          setIsSigningIn(false);
        }
      }
    } else {
      // If the user is only using password then no need for the auth flow type;
      // Verify username and password as a first step, without One-Time-Password;
      try {
        const { nextStep } = await signIn({
          username: formData.username,
          password: formData.password,
          options: {
            authFlowType: 'CUSTOM_WITH_SRP',
          },
        });

        switch (nextStep.signInStep) {
          // DONE
          case SIGN_IN_STEP.DONE:
            log.info('cognitoSignIn without OTP - DONE');
            await handleSuccessfulSignIn();
            break;
          // EMAIL
          case SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE:
            log.info(
              'cognitoSignIn without OTP - CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
            );
            if (nextStep.additionalInfo['mfaType'] === MFA_TYPE.EMAIL) {
              setShowOTPInputBox({
                ifShow: true,
                otpType: MFA_TYPE.EMAIL,
              });
              setIsSigningIn(false);
            } else {
              //! Direct sign in with password
              formData.OTP = MFA_TYPE.NOMFA;
              cognitoSignIn();
            }
            break;
          case SIGN_IN_STEP.CONFIRM_SIGN_IN_WITH_TOTP_CODE:
            log.info(
              'cognitoSignIn without OTP - CONFIRM_SIGN_IN_WITH_TOTP_CODE'
            );
            setShowOTPInputBox({
              ifShow: true,
              otpType: MFA_TYPE.TOTP,
            });
            setIsSigningIn(false);
            break;
          default:
            log.info('cognitoSignIn without OTP - NOT DONE');
            break;
        }
      } catch (error) {
        log.error('Error occurred', error);
        if (
          error.name &&
          ['UserNotFoundException', 'NotAuthorizedException'].includes(
            error.name
          )
        ) {
          setShowWrongPasswordWarning(true);
          setIsSigningIn(false);
        }
      }
    }
  };

  const handleSignInButtonClick = async (event) => {
    event.preventDefault();
    await cognitoSignIn();
  };

  const handleInput = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    setShowWrongPasswordWarning(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const loginMode = 0;
  const colors = pallete[loginMode];

  return (
    <BasicLayout image={newBgImage} loginMode={loginMode}>
      <Card
        id="BasicLayoutChildrenCard"
        sx={{ backgroundColor: colors.cardBackgroundColor }}>
        <Box
          sx={{
            backgroundColor: colors.mainBoxBackgroundColor,
            borderRadius: '10px',
          }}
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center">
          <Typography variant="h5" fontWeight="bold" color="white" mt={1}>
            {intl.get('login_service_name')}
          </Typography>
        </Box>
        <Box pt={4} pb={3} px={3} sx={{ width: '100%' }}>
          <Box component="form" role="form" onSubmit={handleSignInButtonClick}>
            <Box mb={1}>
              <Typography style={{ color: 'white' }}>
                {intl.get('login_label_username_or_email')}
              </Typography>
            </Box>
            <Box mb={2}>
              <StyledMDInput
                type="username"
                required
                fullWidth
                id="username"
                name="username"
                value={formData.username}
                autoComplete="username"
                onChange={handleInput}
                sx={{ input: { color: 'black !important' } }}
              />
            </Box>
            <Box mb={1}>
              <Typography style={{ color: 'white' }}>
                {intl.get('login_label_password')}
              </Typography>
            </Box>
            <Box mb={2}>
              <StyledMDInput
                required
                fullWidth
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                value={formData.password}
                autoComplete="current-password"
                onChange={handleInput}
                sx={{ input: { color: 'black !important' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        color="info">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {showWrongPasswordWarning && (
              <Typography
                variant="body2"
                fontWeight="light"
                style={{ color: 'red' }}>
                {intl.get('login_incorrect_username_password')}
              </Typography>
            )}
            {isAuthError && (
              <Typography
                variant="body2"
                fontWeight="light"
                style={{ color: 'red' }}>
                {intl.get(
                  AUTH_ERROR_MESSAGES[authError] || 'login_unknown_error'
                )}
              </Typography>
            )}
            {showOTPInputBox.ifShow && (
              <div>
                <Box mb={1}>
                  {showOTPInputBox.otpType === 'EMAIL' && (
                    <Typography style={{ color: 'white' }}>
                      {intl.get('login_enter_code')}
                    </Typography>
                  )}
                  {showOTPInputBox.otpType === 'TOTP' && (
                    <Typography style={{ color: 'white' }}>
                      {intl.get('login_enter_otp')}
                    </Typography>
                  )}
                </Box>
                <Box mb={2}>
                  <StyledMDInput
                    required
                    fullWidth
                    id="OTP"
                    name="OTP"
                    autoComplete=""
                    onChange={handleInput}
                    sx={{ input: { color: 'black !important' } }}
                  />
                </Box>
                {showWrongOTPWarning && (
                  <div>
                    <Typography
                      variant="body2"
                      fontWeight="light"
                      style={{ color: 'red' }}>
                      {intl.get('login_invalid_authentication_code')}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="light"
                      style={{ color: 'red' }}>
                      {intl.get('login_authentication_code_attempts_text')}
                    </Typography>
                  </div>
                )}
                {wrongOTPFor3Times && (
                  <Typography
                    variant="body2"
                    fontWeight="light"
                    style={{ color: 'red' }}>
                    {intl.get('login_retry_authentication')}
                  </Typography>
                )}
              </div>
            )}
            <Box mt={4} mb={1}>
              <MDLoadingButton
                loadingPosition="start"
                loading={isSigningIn}
                disabled={isSigningIn}
                type="submit"
                variant="gradient"
                fullWidth
                sx={{
                  backgroundColor: colors.loadingButtonColor,
                  '&:hover': {
                    backgroundColor: colors.loadingButtonColor,
                  },
                  '&:disabled': {
                    backgroundColor: colors.loadingButtonColor,
                  },
                }}>
                <Typography variant="h5" fontWeight="bold" color="white">
                  {isSigningIn
                    ? intl.get('login_please_wait')
                    : intl.get('welcome_button_log_in')}
                </Typography>
              </MDLoadingButton>
            </Box>
            {!showOTPInputBox.ifShow && (
              <Box mt={2} mb={1}>
                <Link to="/forgot-password">
                  <Typography
                    component="span"
                    variant="body2"
                    style={{ color: 'white' }}>
                    {intl.get('login_link_forgot_password')}
                  </Typography>
                </Link>
              </Box>
            )}
          </Box>
        </Box>
      </Card>
      {/* Version display in bottom left corner */}
      <Box
        sx={{
          position: 'fixed',
          bottom: '10px',
          left: '10px',
          zIndex: zIndexValues.navLeft,
        }}>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.75rem',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            padding: '2px 6px',
            borderRadius: '4px',
          }}>
          v{import.meta.env.VITE_APP_VERSION}
        </Typography>
      </Box>
    </BasicLayout>
  );
}

export default WelcomePage;
